import { LoggerService } from './../logger/logger.service';
import './../model/backendmodel';
import { AuthService } from '../services/authentication/auth.service';
import {
    Http,
    Headers,
    Response
} from 'app/services/http';
import { GlobalVariables } from '../services/globals';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { convertObjectToString, format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { DataPlaneAuthService } from 'app/services/data-plane-auth.service';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { Injectable } from '@angular/core';
import { CategoryType } from 'app/shared/utils/connectorCategory.util';
import { UIControlType } from 'app/services/datatype-to-expressiontype';
import { IValidatorMetadata } from 'app/extensibility/metadatas/validator/validator-metadata';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { IMetadataClient } from 'app/services/base-metadata-client';
import { MetadataClientFactoryService } from 'app/services/metadata-client/metadata-client-factory.service';
import { ISchemaTableBrowseSetting } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { IIconMetadata } from 'app/extensibility/metadatas/icon-metadata';

export type ConnectorDataType = 'Binary' | 'Tabular' | 'Hierarchical';

export interface IUIControlsItem {
    bind?: string | { [type: string]: string }; // bind is required in the most cases, while it can be null in itemUiControl of the array container
    dataType?: string; // data type definition in the corresponding setting item
    required?: boolean;
    controlType?: UIControlType;
    displayName?: string;
    description?: string;
    placeholder?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValue?: any;
    validators?: IValidatorMetadata[];
    controlSettings?: {
        // radio, dropdown, select dropdown
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options?: Array<ISelectOption<any>>;
        /**
         * For file location, connection string and schema table selection
         *
         * In dataflow schema table selection, properties are not empty means this is `combined schema table selection`,
         * the properties will be converted to components in edit mode.
         */
        properties?: IUIControlsItem[];
        // array container
        itemUiControl?: IUIControlsItem;
        // azure resource
        resourceTypes?: string[];
        resourceDisplayName?: string;
        resourceConvertTemplate?: string;
        supportListKey?: boolean;
        listKeyAction?: string;
        listKeyApiVersion?: string;
        keyResponseKeywords?: string[];
        supportSubresource?: boolean;
        subresourceApiVersion?: string;
        subresourceCommand?: string;
        // file location
        pattern?: string;
        fileNameProperty?: string;
        // connection string
        combineRule?: 'concat' | 'odbc' | 'sql';
        sensitiveProperties?: string[];
        // dataflow schema table selection
        browseSettings?: ISchemaTableBrowseSetting;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [name: string]: any;
    };
}

export interface IAdfAppModelConfigurationItem {
    properties?: string[];
    uiControls?: IUIControlsItem[];
    itemSettings?: {
        notSupportSchema?: boolean;
        notSupportImportSchema?: boolean;
        notSupportPreviewData?: boolean;
        dataflowUseInlineProjection?: boolean;
        dataflowType?: string
        dataflowFormat?: string
        dataflowConnectTransformationProperties?: string[] | Record<string, string>;

        [name: string]: unknown;
    };
}

export interface IAdfAppModelConfiguration {
    displayName?: string;
    icon?: IIconMetadata;
    category?: CategoryType[];
    linkedService?: IAdfAppModelConfigurationItem;
    location?: IAdfAppModelConfigurationItem;
    storeReadSettings?: IAdfAppModelConfigurationItem;
    storeWriteSettings?: IAdfAppModelConfigurationItem;
    dataset?: IAdfAppModelConfigurationItem;
    copySource?: IAdfAppModelConfigurationItem;
    copySink?: IAdfAppModelConfigurationItem;
    dataflowInlineSource?: IAdfAppModelConfigurationItem;
    dataflowInlineSink?: IAdfAppModelConfigurationItem;
}

export interface IPluginSettingsProperty {
    name?: string;
    type?: string;
    signature?: boolean;
    sensitive?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValue?: any;
    required?: boolean;
    displayName?: string;
    pattern?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [name: string]: any;
}

export interface IPluginMetadataSucceededResult {
    connectorName: string;
    dataType: ConnectorDataType;
    connectionSettings: IPluginSettingsProperty[];
    sensitiveFields?: string[];
    signatureFields?: string[];
    readSettings?: IPluginSettingsProperty[];
    writeSettings?: IPluginSettingsProperty[];
    adfAppModelConfiguration?: IAdfAppModelConfiguration;
    SupportEncryption?: boolean;
    IsDynamicLoadPlugin?: boolean;
}

export interface IPluginMetadataError {
    Code: number;
    Message: string;
    Category?: number;
    EventType?: number;
}

export interface IPluginMetadataResultItem {
    state: number;
    pluginName: string;
    error?: IPluginMetadataError;
    result?: IPluginMetadataSucceededResult;
}

export interface IPluginMetadataAPIResult {
    pluginMetadata: IPluginMetadataResultItem[];
}

@Injectable({
    providedIn: 'root'
})
export class PluginMetadataApiService {
    public static className: string = 'PluginMetadataApiService';

    private readonly _metadataClient: IMetadataClient;
    public constructor(
        protected readonly _httpClient: Http,
        protected readonly _authService: AuthService,
        protected readonly _urlSvc: FactoryUrlInformationService,
        private readonly _dataPlaneAuthService: DataPlaneAuthService,
        private readonly _metadataClientService: MetadataClientFactoryService,
        private readonly _logger: LoggerService) {
        this._metadataClient = this._metadataClientService.getClient(true);
    }

    public async getDynamicLayoutMetadataAsync(irName?: string): Promise<IPluginMetadataSucceededResult[]> {
        const activityId = generateUUID();
        const headers = await this._getDefaultHeadersAsync(activityId);
        let dynamicMetadataUrl;
        if (__IS_A365__) {
            dynamicMetadataUrl = !stringIsNullOrEmpty(irName) ?
                await this._getDynamicLayoutMetadataFromSHIRUrl(this._metadataClient.getBaseUri(), irName, GlobalVariables.dynamicPluginTypes) :
                await this._getDynamicLayoutMetadataFromServiceUrl(this._metadataClient.getBaseUri(), GlobalVariables.dynamicPluginTypes);
        } else if (await this._canUseDataPlane()) {
            const baseUrl = ((await this._dataPlaneAuthService.getDataPlaneAccess()).dataPlaneUrl) + this._urlSvc.getResourceId();
            dynamicMetadataUrl = !stringIsNullOrEmpty(irName) ? await this._getDynamicLayoutMetadataFromSHIRUrl(baseUrl, irName, GlobalVariables.dynamicPluginTypes) :
                await this._getDynamicLayoutMetadataFromServiceUrl(baseUrl, GlobalVariables.dynamicPluginTypes);
        } else {
            // log the call for monitoring
            const message = format(stringResources.DoNotHavePermissionToGetDataPlaneAccess, '');
            this._logger.error(PluginMetadataApiService.className, `Failed to get DataPlane access, reason: ${message}`);
            return [];
        }
        if (dynamicMetadataUrl != null) {
            const payload = !stringIsNullOrEmpty(irName) ? {
                activityId,
                tags: {},
                userAgent: 'Madrid'
            } : undefined;
            const result = await this._post(dynamicMetadataUrl, payload, { headers }, activityId);
            if (result != null && (result.json() as IPluginMetadataAPIResult).pluginMetadata != null) {
                const pluginResult = (result.json() as IPluginMetadataAPIResult).pluginMetadata;
                // log the failed plugins' message
                const failedPluginsMessage = pluginResult.filter((item) => item && item.state === 0 && item.error != null).map((item) => item.error.Message).join('/');
                this._logger.error(PluginMetadataApiService.className, failedPluginsMessage);
                // return the succeeded plugins
                return (pluginResult.filter((item) => item && item.state === 1 && item.result != null
                    && (!stringIsNullOrEmpty(irName) || item.result.IsDynamicLoadPlugin)).map((item) => item.result)) || [];
            }
        }

        return [];
    }

    private async _getDynamicLayoutMetadataFromSHIRUrl(baseUrl: string, irName: string, plugins?: string): Promise<string> {
        this._logger.log(PluginMetadataApiService.className, `Hit Getting Plugin Metadata From SHIR`);
        const result = `${baseUrl}/integrationRuntimes/${irName}/getPluginMetadata`;
        if (stringIsNullOrEmpty(plugins)) {
            return result + '?api-version=' + GlobalVariables.dataFactoryVersion;
        }
        return result + '?plugins=' + plugins + '&api-version=' + GlobalVariables.dataFactoryVersion;
    }

    private async _getDynamicLayoutMetadataFromServiceUrl(baseUrl: string, plugins?: string): Promise<string> {
        this._logger.log(PluginMetadataApiService.className, `Hit Getting Plugin Metadata From Service`);
        const result = baseUrl + '/getIntegrationRuntimeMetadata';
        if (stringIsNullOrEmpty(plugins)) {
            return result + '?api-version=' + GlobalVariables.dataFactoryVersion;
        }
        return result + '?plugins=' + plugins + '&api-version=' + GlobalVariables.dataFactoryVersion;
    }

    private async _canUseDataPlane(): Promise<boolean> {
        if (!GlobalVariables.enableDataPlane) {
            return false;
        }
        return await this._dataPlaneAuthService.canGetDataPlaneAccess();
    }

    private async _getDefaultHeadersAsync(activityId: string): Promise<Headers> {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        headers.append('x-ms-client-request-id', activityId);
        if (await this._canUseDataPlane()) {
            headers.append('x-ms-dataplane-token', (await this._dataPlaneAuthService.getDataPlaneAccess()).accessToken);
        } else if (__IS_A365__) {
            headers.append('Authorization', `Bearer ${await this._authService.getToken(this._metadataClient.getAuthArea())}`);
        }
        return headers;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _post(url: string, body: any, options: any, activityId: string): Promise<Response> {
        try {
            return await this._httpClient.post(url, body, options).toPromise();
        } catch (e) {
            const activityIdMessage = `${stringResources.ActivityId}:${activityId}`;
            this._logger.warn(PluginMetadataApiService.className, `_post - Error while calling ${url}. Status: ${e.status}. ${activityIdMessage}.`);
            if (e.status === 504) {
                const message = `${stringResources.GatewayTimeOutError} ${activityIdMessage}`;
                this._logger.warn(PluginMetadataApiService.className, message);
                throw message;
            } else if (e.status === 0) {
                const message = `${e.statusText}. ${activityIdMessage}`;
                // When status is 0, we do need to log the error object for more information
                this._logger.error(PluginMetadataApiService.className, `_post - Failed with status 0. Url: ${url}. Error: ${convertObjectToString(e)}. ${activityIdMessage}`);
                throw message;
            } else if (e.status < 500 && e.status >= 200) {
                // user error
                if (e._body) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const result = (e._body.error != null ? JSON.parse(e._body.error) : JSON.parse(e._body)) as any;
                    const message = `${result.message || (result.error != null ? result.error.message : '')}. ${activityIdMessage}`;
                    throw message;
                } else if (e.error) {
                    const content = JSON.parse(e.error);
                    const message = `${content.message != null ? content.message : e.error}. ${activityIdMessage}`;
                    throw message;
                } else {
                    const message = `${JSON.stringify(e)}. ${activityIdMessage}`;
                    throw message;
                }
            } else {
                // internal server error
                const message = `${e.statusText}. ${activityIdMessage}`;
                this._logger.error(PluginMetadataApiService.className, message);
                throw message;
            }
        }
    }
}
